import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { combineLatest, filter, map, first } from 'rxjs';

import { MessageService } from '@openng/optimus-ui/api';

import { EnvironmentService } from 'src/app/services/environment.service';
import { AuthService } from 'src/app/services/auth.service';

/**
 * Query-param key carrying the sender's environment on a shared link (e.g. ?env=TREO). When a user
 * shares a link to any environment-scoped page, the link encodes the environment they were in so the
 * recipient's view switches to match, instead of silently rendering under the recipient's own
 * (possibly different) selected environment.
 */
export const ENV_QUERY_PARAM = 'env';

/**
 * Guard for environment-scoped routes that honours the ?env= query param on a shared link.
 *
 * On navigation it waits until the user's environment list has loaded, then:
 *  - if the link carries an ?env= the user is allowed to select, switches the selected environment to
 *    it (so the whole app — header, data, suite-details view — reflects the shared environment);
 *  - if the link carries an ?env= the user is NOT allowed to select, shows an explicit "no access"
 *    message and redirects to the dashboard rather than rendering data under the wrong environment;
 *  - if there is no ?env= (older links, direct navigation), falls back to requiring any selected
 *    environment, redirecting to the dashboard when none is set.
 *
 * Running as a CanActivate guard means the switch happens BEFORE the target component activates, so
 * adopting the link's environment never trips a component's "environment changed -> go to dashboard"
 * subscription.
 * @param route
 */
export const urlEnvironmentGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
	const environmentService = inject(EnvironmentService);
	const authService = inject(AuthService);
	const messageService = inject(MessageService);
	const router = inject(Router);

	const linkEnv = route.queryParamMap.get(ENV_QUERY_PARAM);

	return combineLatest({
		loggedIn: authService.isLoggedIn$,
		loaded: environmentService.environmentLoaded$,
		available: environmentService.availableEnvironments$,
	}).pipe(
		// Only decide once the user is signed in AND their environment list has loaded
		filter(({ loggedIn, loaded }) => loggedIn && loaded),
		first(),
		map(() => {
			// No environment on the link: keep the existing "must have a selected environment" rule.
			if (!linkEnv) {
				return environmentService.getCurrentEnvironment() ? true : router.createUrlTree(['/']);
			}

			const canonicalEnv = environmentService.resolveAvailableEnvironment(linkEnv);
			if (!canonicalEnv) {
				messageService.add({
					severity: 'error',
					summary: 'No access to environment',
					detail: `You do not have access to the '${linkEnv}' environment, so this link cannot be opened.`,
					life: 6000,
				});
				return router.createUrlTree(['/']);
			}

			// Adopt the shared environment if it differs from the current selection.
			if (environmentService.getCurrentEnvironment().toLowerCase() !== canonicalEnv.toLowerCase()) {
				environmentService.setEnvironment(canonicalEnv);
			}
			return true;
		}),
	);
};
